import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { SlideElement } from '../types/slide';
import { BRAND, BRAND_COLORS, CANVAS } from '../lib/brand';
import { useDeckStore } from '../store/deckStore';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';

interface Props {
  element: SlideElement;
  scale: number;
}

const FONT_SIZES = [14, 18, 24, 32, 40, 48];

const POLISH_ACTIONS = [
  ['shorten', 'Shorten for execs'],
  ['bullets', 'Punchy bullets'],
  ['action_title', 'Action-oriented title'],
  ['executive', 'More concise'],
  ['grammar', 'Fix grammar and tone'],
] as const;

type PolishAction = (typeof POLISH_ACTIONS)[number][0];

export function FloatingToolbar({ element, scale }: Props) {
  const updateElement = useDeckStore((s) => s.updateElement);
  const pushHistory = useDeckStore((s) => s.pushHistory);
  const aiEnabled = useAuthStore((s) => s.aiEnabled);
  const [sizeAnchor, setSizeAnchor] = useState<HTMLElement | null>(null);
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const [aiAnchor, setAiAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const left = element.x * scale;
  const top = Math.max(8, element.y * scale - 52);
  const color = element.color ?? BRAND.ink;
  const bold = (element.fontWeight ?? 400) >= 600;

  const patch = (p: Partial<SlideElement>) => {
    pushHistory();
    updateElement(element.id, p);
  };

  const polish = async (action: PolishAction) => {
    if (!element.text) return;
    setBusy(true);
    try {
      const res = await api.polishText(element.text, action);
      patch({ text: res.text });
      setAiAnchor(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rewrite failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: 0.5,
        left,
        top,
        maxWidth: CANVAS.width * scale,
        boxShadow: '0 4px 12px -2px rgba(16,24,40,0.12)',
      }}
    >
      <Button
        size="small"
        color="neutral"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={(e) => setSizeAnchor(e.currentTarget)}
        aria-label="Font size"
        sx={{ minWidth: 0, px: 1 }}
      >
        {element.fontSize ?? 18}
      </Button>
      <Menu anchorEl={sizeAnchor} open={Boolean(sizeAnchor)} onClose={() => setSizeAnchor(null)}>
        {FONT_SIZES.map((size) => (
          <MenuItem
            key={size}
            selected={element.fontSize === size}
            onClick={() => {
              patch({ fontSize: size });
              setSizeAnchor(null);
            }}
            sx={{ minWidth: 72 }}
          >
            {size}
          </MenuItem>
        ))}
      </Menu>

      <Tooltip title="Text color">
        <IconButton size="small" aria-label="Text color" onClick={(e) => setColorAnchor(e.currentTarget)}>
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: color,
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
            }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={colorAnchor}
        open={Boolean(colorAnchor)}
        onClose={() => setColorAnchor(null)}
        slotProps={{ list: { sx: { p: 1 } } }}
      >
        <Stack direction="row" spacing={0.75}>
          {BRAND_COLORS.map((c) => {
            const active = color.toUpperCase() === c.hex;
            return (
              <Tooltip key={c.hex} title={c.name}>
                <ButtonBase
                  aria-label={c.name}
                  aria-pressed={active}
                  onClick={() => {
                    patch({ color: c.hex });
                    setColorAnchor(null);
                  }}
                  sx={(t) => ({
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: c.hex,
                    border: active ? `2px solid ${t.palette.background.paper}` : `1px solid ${t.palette.chrome.border}`,
                    boxShadow: active ? `0 0 0 2px ${t.palette.primary.main}` : 'none',
                  })}
                />
              </Tooltip>
            );
          })}
        </Stack>
      </Menu>

      <Tooltip title="Bold">
        <IconButton
          size="small"
          aria-label="Bold"
          aria-pressed={bold}
          onClick={() => patch({ fontWeight: bold ? 400 : 700 })}
          sx={bold ? { bgcolor: 'chrome.subtle', color: 'text.primary' } : undefined}
        >
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {aiEnabled && (
        <>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

      <Button
        size="small"
        color="primary"
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeOutlinedIcon />}
        onClick={(e) => setAiAnchor(e.currentTarget)}
      >
        Rewrite
      </Button>
      <Menu
        anchorEl={aiAnchor}
        open={Boolean(aiAnchor)}
        onClose={() => setAiAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 200 } } }}
      >
        {POLISH_ACTIONS.map(([action, label]) => (
          <MenuItem key={action} disabled={busy} onClick={() => void polish(action)}>
            {label}
          </MenuItem>
        ))}
      </Menu>
        </>
      )}

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
